// Add product by URL (TDC-1499): paste a PDP URL, the API fetches the page
// synchronously, imports the product (category + brand associations) and
// creates the PDP URL with an initial scoring job. Replaces the manual
// product form as the brand portal's Add product flow — editing an existing
// product keeps the form (row menu).

import React from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import { rmnImportProductUrlEndpoint } from "../../../../api/endpoints";
import { EditUrlDialog } from "../../../network/EditUrlDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  // Fired after a successful import so the listing refreshes.
  onImported: () => void;
}

export function AddProductFromUrl({ open, onClose, onImported }: Props) {
  return (
    <EditUrlDialog
      open={open}
      title="Add product"
      label="Product page URL (PDP)"
      helperText="Paste the product's page — we'll fetch it, import the product and start scoring the page. This can take up to a minute."
      initialUrl=""
      allowClear={false}
      saveLabel="Import"
      savingLabel="Importing…"
      onClose={onClose}
      onSave={async (url) => {
        try {
          const { data } = await axiosInstant.post(rmnImportProductUrlEndpoint, {
            url,
          });
          toast.success(
            data?.created === false
              ? `${data?.title || "That product"} was already in your catalog — the page was linked to it.`
              : `Imported ${data?.title || "the product"}.`
          );
          onImported();
        } catch (e: any) {
          const status = e?.response?.status;
          const code = e?.response?.data;
          if (code === "DomainNotAllowed") {
            throw new Error("That URL isn't on your network's domains.");
          }
          if (code === "NotAProductPage") {
            throw new Error(
              "That page doesn't look like a product page — paste a specific product's URL."
            );
          }
          if (code === "PageFetchFailed") {
            throw new Error(
              "We couldn't fetch that page. Check the URL and try again."
            );
          }
          if (status === 429) {
            throw new Error(
              "You've reached the hourly limit for URL imports. Try again later."
            );
          }
          throw new Error("Couldn't import the product. Please try again.");
        }
      }}
    />
  );
}
